import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

// Synchronously hydrate from localStorage so the first paint already has user
// state and most pages don't have to wait for /auth/me before rendering.
const readPersistedUser = () => {
    try {
        const token = localStorage.getItem('leadhub_token');
        const raw = localStorage.getItem('leadhub_user');
        if (token && raw) {
            return JSON.parse(raw);
        }
    } catch {
        // corrupt JSON — fall through and clear below.
    }
    return null;
};

export const AuthProvider = ({ children }) => {
    const persisted = readPersistedUser();
    const [user, setUser] = useState(persisted);
    // Optimistic: if we have a cached user, treat as authenticated and verify
    // in the background. This kills the multi-second loader on every navigation.
    const [loading, setLoading] = useState(!persisted);
    const [isAuthenticated, setIsAuthenticated] = useState(!!persisted);

    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('leadhub_token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await authAPI.getMe();
            setUser(response.data);
            setIsAuthenticated(true);
            // Refresh the cached user so optimistic hydration stays accurate.
            try {
                localStorage.setItem('leadhub_user', JSON.stringify(response.data));
            } catch { /* quota / private-mode */ }
        } catch (err) {
            // Token rejected by server — clear local state and bounce to login.
            localStorage.removeItem('leadhub_token');
            localStorage.removeItem('leadhub_user');
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const response = await authAPI.login(email, password);
        const { token, user: userData } = response.data;
        localStorage.setItem('leadhub_token', token);
        localStorage.setItem('leadhub_user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('leadhub_token');
        localStorage.removeItem('leadhub_user');
        setUser(null);
        setIsAuthenticated(false);
    };

    const hasRole = (...roles) => {
        return user && roles.includes(user.role);
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAuthenticated,
            login,
            logout,
            hasRole,
            checkAuth
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
